import { PayoutRevenue } from '@/types';
import { formatCurrency } from '@/utils/currencyUtils';
import getMonthNameFromIndex from './getMonthNameFromIndex';
import { useEffect } from 'react';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';

interface PayoutsOverTimeLineGraphTooltipContentProps {
  payload: PayoutRevenue[number];
  chartLineData: { year: string; color: string }[];
}

/**
 * Content component for chart tooltips displaying value and currency information
 * @param payload - The payload of the tooltip.
 * @param visibleCategories - The visible categories for the campaign types modal.
 */
const PayoutsOverTimeLineGraphTooltipContent = ({
  payload,
  chartLineData,
}: PayoutsOverTimeLineGraphTooltipContentProps) => {
  const monthName = payload?.month
    ? getMonthNameFromIndex({ index: parseInt(payload.month) - 1, variant: 'long' })
    : undefined;

  useEffect(() => {
    if (!monthName) {
      return;
    }

    const timeout = setTimeout(() => {
      mixpanelTrack(MIXPANEL_EVENT.payouts.overTimeChart.tooltipOpened, {
        widget: RevenueDashboardWidget.PayoutsOverTime,
        page: 'payouts',
        month: monthName,
      });
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [monthName]);

  if (!payload) {
    return null;
  }

  const lineColors = chartLineData.reduce(
    (acc, { year, color }) => {
      acc[year] = color;
      return acc;
    },
    {} as Record<string, string>
  );

  return (
    <div className="flex flex-col gap-1 p-1 text-sm">
      <p className="font-medium capitalize mr-6">{monthName}</p>
      <ul>
        {Object.entries(payload.totals)
          .sort(([a], [b]) => Number(b) - Number(a))
          .map(([key, value]) => (
            <li key={key} className="flex items-center">
              <span>
                <figure
                  className={`w-3 h-3 inline-block mr-1`}
                  style={{ backgroundColor: lineColors[key] }}
                />{' '}
                {key}: <strong>{formatCurrency(value.amount, value.currency, true)}</strong>
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
};

export default PayoutsOverTimeLineGraphTooltipContent;
